export interface ExportShapeLike {
  type: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  rotation?: number;
  props: Record<string, unknown>;
}

export interface PersonLabelGeometryContext {
  line2Field: string | null;
}

interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

const normaliseLineEndings = (value: string): string => value.replace(/\r\n?/g, '\n');

const coalesceString = (...values: Array<unknown>): string | undefined => {
  for (const value of values) {
    if (typeof value !== 'string') continue;
    const trimmed = value;
    if (trimmed.length) return trimmed;
  }
  return undefined;
};

const estimateLineWidthPx = (line: string, fontSize: number): number => {
  // Deterministic heuristic: approximate glyph widths by character class.
  // This avoids underestimating wide glyphs (e.g. 'w') which causes clipping,
  // while reducing overestimation for narrow glyphs.
  const wide = new Set(['w', 'm', 'W', 'M']);
  const narrow = new Set([
    'i', 'l', 'I', 'j', 't', 'f', 'r',
    '.', ',', ':', ';', '\'', '"', '|', '!', '`',
  ]);
  const space = new Set([' ', '\t']);

  let em = 0;
  for (const ch of Array.from(line)) {
    if (wide.has(ch)) em += 0.95;
    else if (narrow.has(ch)) em += 0.35;
    else if (space.has(ch)) em += 0.33;
    else em += 0.62;
  }

  // Add a small buffer for kerning, font substitution, and renderer differences.
  const px = em * fontSize;
  const buffer = Math.max(4, fontSize * 0.2);
  return Math.max(0, px + buffer);
};

const estimateTextBlockSize = (
  text: string,
  fontSize: number,
  lineHeightPx: number,
): { width: number; height: number; lineCount: number } => {
  const normalized = normaliseLineEndings(text).replace(/\n+$/g, '');
  const lines = normalized.split('\n');
  const widths = lines.map((line) => estimateLineWidthPx(line, fontSize));
  const maxWidth = widths.length ? Math.max(...widths) : 0;
  const lineCount = Math.max(1, lines.length);
  // Baseline-based approximation: include ascent + descent plus additional line heights.
  const ascent = fontSize;
  const descent = Math.max(2, fontSize * 0.25);
  const height = ascent + descent + Math.max(0, lineCount - 1) * lineHeightPx;
  return { width: maxWidth, height, lineCount };
};

const rotatePoint = (
  point: { x: number; y: number },
  center: { x: number; y: number },
  radians: number,
): { x: number; y: number } => {
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return {
    x: center.x + dx * cos - dy * sin,
    y: center.y + dx * sin + dy * cos,
  };
};

const aabbForRotatedRect = (rect: Bounds, center: { x: number; y: number }, radians: number): Bounds => {
  const corners = [
    { x: rect.minX, y: rect.minY },
    { x: rect.maxX, y: rect.minY },
    { x: rect.maxX, y: rect.maxY },
    { x: rect.minX, y: rect.maxY },
  ].map((pt) => rotatePoint(pt, center, radians));

  const xs = corners.map((c) => c.x);
  const ys = corners.map((c) => c.y);

  return {
    minX: Math.min(...xs),
    minY: Math.min(...ys),
    maxX: Math.max(...xs),
    maxY: Math.max(...ys),
  };
};

const unionBounds = (a: Bounds, b: Bounds): Bounds => ({
  minX: Math.min(a.minX, b.minX),
  minY: Math.min(a.minY, b.minY),
  maxX: Math.max(a.maxX, b.maxX),
  maxY: Math.max(a.maxY, b.maxY),
});

export function computeGenogramPersonLabelBounds(shape: ExportShapeLike): Bounds | null {
  if (shape.type !== 'person') return null;

  const attrs =
    shape.props && typeof shape.props.attrs === 'object' && shape.props.attrs !== null
      ? (shape.props.attrs as Record<string, unknown>)
      : {};

  const nameText = coalesceString(shape.props.name, attrs.name) ?? '';

  const exportLine2FieldRaw = coalesceString((shape.props as any).__exportLine2Field, (attrs as any).__exportLine2Field) ?? null;
  const resolveSecondLabelText = (fieldId: string | null): string => {
    if (!fieldId) return '';
    const key = fieldId === 'participantId' ? 'researchId' : fieldId;
    const fromKey = (k: string) => coalesceString((shape.props as any)[k], (attrs as any)[k]);

    if (fieldId === 'gender') {
      return fromKey('genderIdentity') ?? fromKey('gender') ?? '';
    }
    if (fieldId === 'educationLevel') {
      return fromKey('educationLevel') ?? fromKey('education') ?? '';
    }
    return fromKey(key) ?? '';
  };

  const secondLabelText = resolveSecondLabelText(exportLine2FieldRaw);
  if (!nameText.trim() && !secondLabelText.trim()) return null;

  const rawAlign = typeof attrs.textAlign === 'string' ? attrs.textAlign : 'center';
  const effectiveAlign = rawAlign === 'right' ? 'right' : rawAlign === 'center' ? 'center' : 'left';

  const size = Math.max(1, Math.min(shape.size.width, shape.size.height));
  const rawFontSize = Number((attrs as any).fontSize);
  const nameFontSize = Number.isFinite(rawFontSize) && rawFontSize > 0 ? rawFontSize : 12;
  const rawLineHeight = Number((attrs as any).lineHeight);
  const lineHeightMultiplier =
    Number.isFinite(rawLineHeight) && rawLineHeight > 0
      ? Math.min(3, Math.max(0.5, rawLineHeight))
      : 1.2;
  const nameLineHeight = nameFontSize * lineHeightMultiplier;
  const secondLabelFontSize = 10;
  const secondLabelLineHeight = secondLabelFontSize * lineHeightMultiplier;

  const anchorXLocal = effectiveAlign === 'right' ? size : effectiveAlign === 'center' ? size / 2 : 0;

  const boundsLocal: Bounds[] = [];

  if (nameText.trim()) {
    const block = estimateTextBlockSize(nameText, nameFontSize, nameLineHeight);
    const startX =
      effectiveAlign === 'right'
        ? anchorXLocal - block.width
        : effectiveAlign === 'center'
          ? anchorXLocal - block.width / 2
          : anchorXLocal;
    const topY = size + 5;
    const rect: Bounds = {
      minX: startX,
      minY: topY,
      maxX: startX + block.width,
      maxY: topY + block.height,
    };
    boundsLocal.push(rect);
  }

  if (secondLabelText.trim()) {
    const block = estimateTextBlockSize(secondLabelText, secondLabelFontSize, secondLabelLineHeight);
    const startX =
      effectiveAlign === 'right'
        ? anchorXLocal - block.width
        : effectiveAlign === 'center'
          ? anchorXLocal - block.width / 2
          : anchorXLocal;
    const topY = (() => {
      const defaultTop = size + 20;
      if (!nameText.trim()) return defaultTop;
      const nameBlock = estimateTextBlockSize(nameText, nameFontSize, nameLineHeight);
      const computed = size + 5 + nameBlock.height + 3;
      return Math.max(defaultTop, computed);
    })();
    const rect: Bounds = {
      minX: startX,
      minY: topY,
      maxX: startX + block.width,
      maxY: topY + block.height,
    };
    boundsLocal.push(rect);
  }

  if (boundsLocal.length === 0) return null;

  const combinedLocal = boundsLocal.reduce((acc, next) => unionBounds(acc, next));

  const translate = shape.position;
  const combinedWorld: Bounds = {
    minX: combinedLocal.minX + translate.x,
    minY: combinedLocal.minY + translate.y,
    maxX: combinedLocal.maxX + translate.x,
    maxY: combinedLocal.maxY + translate.y,
  };

  const rotationDeg = typeof shape.rotation === 'number' && Number.isFinite(shape.rotation) ? shape.rotation : 0;
  if (!rotationDeg) {
    return combinedWorld;
  }

  const radians = (rotationDeg * Math.PI) / 180;
  const centerWorld = {
    x: translate.x + size / 2,
    y: translate.y + size / 2,
  };
  return aabbForRotatedRect(combinedWorld, centerWorld, radians);
}

